import React from 'react';
import { BarContainer, BarIcon, BarShineEffect, BarText, Container, Progress, TextContainer } from './styledComponents';

/**
 * Bar component displays a customizable progress bar with gradient fill,
 * optional icon, and optional shine effect.
 *
 * @component
 * @param {Object} props
 * @param {number} props.progress - Current progress value.
 * @param {number} [props.max=100] - Maximum value for progress (to calculate percentage).
 * @param {string} [props.barIcon] - Source for the icon shown beside the bar.
 * @param {Object} [props.iconStyle] - Style overrides for the bar icon.
 * @param {string} [props.label] - Text label shown inside the bar when not showing percentage.
 * @param {Object} [props.labelStyle] - Style overrides for the text inside the bar.
 * @param {Object} [props.percentageStyle] - Style overrides for the percentage text inside the bar.
 * @param {Function} [props.animation] - Animation applied to the bar's width.
 * @param {Object} [props.barContainerStyle] - Style overrides for the bar container.
 * @param {Object} [props.progressStyle] - Style overrides for the bar's progress fill.
 * @param {boolean} [props.shineEffect=false] - Whether to show the shine overlay animation.
 * @param {Object} [props.shineEffectStyles] - Style overrides for the shine effect.
 * @param {boolean} [props.showPercentage=false] - If true, shows the numeric percentage instead of label.
 * @param {boolean} [props.showProgress=false] - If true, animates the fill to reflect progress.
 */
const Bar = ({
  progress,
  max = 100,
  barIcon,
  iconStyle = {},
  label,
  labelStyle = {},
  percentageStyle = {},
  animation = null,
  barContainerStyle = {},
  progressStyle = {},
  shineEffect = false,
  shineEffectStyles = {},
  showPercentage = false,
  showProgress = false,
}) => {
  const percentage = progress !== null && progress !== undefined ? (progress / max) * 100 : null;

  return (
    <>
      <BarContainer animation={animation} barContainerStyle={barContainerStyle}>
        <BarIcon src={barIcon} customStyle={iconStyle} />
        <TextContainer>
          <BarText
            key={showPercentage ? 'percentage' : 'label'}
            customStyle={progress !== null && progress !== undefined && showPercentage ? percentageStyle : labelStyle}
          >
            {showPercentage ? `${percentage.toFixed(0)}%` : label}
          </BarText>
        </TextContainer>
        <Container>
          <Progress
            key={showProgress ? 'progress' : 'progress-label'}
            progress={showProgress && progress}
            progressStyle={progressStyle}
          />
        </Container>
      </BarContainer>
      {shineEffect && <BarShineEffect progress={progress} customStyle={shineEffectStyles} />}
    </>
  );
};

export default Bar;

